/**
 * PrintingSelector - Dropdown for choosing which printing of a card to act on
 *
 * Owned printings sort first and show their quantity, so the user's own copies are one click away.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';

import { calculateDropdownPosition } from '@/hooks/useDropdownPosition';
import type { MinimalCard } from '@/services/cardDatabase';

export interface PrintingSelectorProps {
  value: MinimalCard;
  printings: MinimalCard[];
  /** scryfall_id → owned quantity. */
  ownedPrintings: Map<string, number>;
  onChange: (printing: MinimalCard) => void;
  className?: string;
}

export default function PrintingSelector({ value, printings, ownedPrintings, onChange, className }: PrintingSelectorProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const sortedPrintings = useMemo(() => {
    return [...printings].sort((a, b) => {
      const aOwned = ownedPrintings.get(a.id) ?? 0;
      const bOwned = ownedPrintings.get(b.id) ?? 0;
      if (aOwned > 0 !== bOwned > 0) return aOwned > 0 ? -1 : 1;

      const setCompare = a.set.localeCompare(b.set);
      if (setCompare !== 0) return setCompare;
      return (parseInt(a.collector_number, 10) || 0) - (parseInt(b.collector_number, 10) || 0);
    });
  }, [printings, ownedPrintings]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setPosition(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggle = () => {
    if (isOpen || !containerRef.current) {
      setIsOpen(false);
      setPosition(null);
      return;
    }
    const pos = calculateDropdownPosition(containerRef.current, {
      placement: 'top-start',
      estimatedWidth: 224,
      estimatedHeight: Math.min(sortedPrintings.length * 44, 256),
    });
    setPosition({ top: pos.top, left: pos.left });
    setIsOpen(true);
  };

  return (
    <div className={clsx('relative', className)} ref={containerRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        aria-expanded={isOpen}
        aria-label={`Printing: ${value.set.toUpperCase()} ${value.collector_number}`}
        className={clsx(
          'flex w-full items-center justify-between gap-1 rounded px-2 py-1 text-xs transition-colors',
          'border-storm bg-obsidian hover:border-ash/50 border',
          isOpen && 'border-aether/50',
        )}
      >
        <span className="truncate">
          <span className="text-silver uppercase">{value.set}</span>
          <span className="text-ash mx-1">#{value.collector_number}</span>
          {ownedPrintings.has(value.id) && <span className="text-aether">✓</span>}
        </span>
        <IoChevronDown className={clsx('text-ash h-3 w-3 shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && sortedPrintings.length > 1 && position && (
        <div
          className="border-storm bg-obsidian fixed z-50 max-h-64 w-56 overflow-y-auto rounded border shadow-xl"
          style={{ top: position.top, left: position.left }}
        >
          {sortedPrintings.map((printing) => {
            const ownedQty = ownedPrintings.get(printing.id) ?? 0;
            const isSelected = printing.id === value.id;
            return (
              <button
                key={printing.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(printing);
                  setIsOpen(false);
                  setPosition(null);
                }}
                className={clsx(
                  'flex w-full items-center gap-2 px-2.5 py-2 text-left text-sm transition-colors',
                  isSelected ? 'bg-aether/20 text-pearl' : 'text-silver hover:bg-storm',
                  ownedQty > 0 && !isSelected && 'bg-aether/5',
                )}
              >
                <div className="h-10 w-7 shrink-0 overflow-hidden rounded-sm">
                  {printing.image_uris?.small ? (
                    <img src={printing.image_uris.small} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="bg-slate h-full w-full" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="uppercase">{printing.set}</span>
                  <span className="text-ash ml-1">#{printing.collector_number}</span>
                </div>
                {ownedQty > 0 && <span className="text-aether shrink-0 font-medium">×{ownedQty}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
